import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { Example } from "../components/demoSidebar/Example";
import { Bot } from "lucide-react";

const generateChartData = () => [
  { name: "Jan", value: Math.floor(Math.random() * 100) + 50 },
  { name: "Feb", value: Math.floor(Math.random() * 100) + 50 },
  { name: "Mar", value: Math.floor(Math.random() * 100) + 50 },
  { name: "Apr", value: Math.floor(Math.random() * 100) + 50 },
  { name: "May", value: Math.floor(Math.random() * 100) + 50 },
];

const DemoPage = () => {
  const [chartData, setChartData] = useState(generateChartData());
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setChartData(generateChartData());
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const fullHistory = useMemo(() => [
    { role: "user", content: "What is Blackbear?" },
    {
      role: "assistant",
      content:
        "Blackbear is a tech company specializing in business solutions that drive efficiency, improved productivity, and streamline operations by providing an array of services from AI Powered Solutions, Web App Development, ERP Solutions, Process Automations, and Data Analytics. Visit our services page here to find out more.",
    },
  ], []);

  const [chatHistory, setChatHistory] = useState([]);
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");

  useEffect(() => {
    if (messageIndex < fullHistory.length) {
      const currentMsg = fullHistory[messageIndex];
      let timeoutId;

      if (charIndex < currentMsg.content.length) {
        timeoutId = setTimeout(() => {
          setCurrentText((prev) => prev + currentMsg.content[charIndex]);
          setCharIndex((prev) => prev + 1);
        }, 30);
      } else {
        setChatHistory((prev) => [...prev, { ...currentMsg, content: currentText }]);
        setCurrentText("");
        setCharIndex(0);
        setMessageIndex((prev) => prev + 1);
      }

      return () => clearTimeout(timeoutId);
    }
  }, [charIndex, messageIndex, currentText, fullHistory]);

  const displayMessages = [...chatHistory];
  if (messageIndex < fullHistory.length && currentText) {
    displayMessages.push({ ...fullHistory[messageIndex], content: currentText });
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5] dark:bg-slate-900 text-[#111] dark:text-white px-4 sm:px-6 py-16 sm:py-20 font-openSans transition-colors">
      {/* Header */}
      <header className="mb-10 text-center mt-20 px-2">
        <h1 className="text-2xl sm:text-4xl font-bold mb-2">
          Interactive Demo Showcase
        </h1>
        <p className="text-gray-600 dark:text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
          A showcase of stunning UI/UX components used across Blackbear’s platform.
        </p>
      </header>

      {/* Grid of Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 max-w-7xl mx-auto">
        {/* Feature 1: Typewriter */}
        <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl shadow-md flex flex-col transition-colors">
          <h2 className="text-lg sm:text-xl font-bold text-blue-700 dark:text-blue-300 mb-4 text-left">
            Typewriter Effect
          </h2>
          <div className="typewriter-container w-full text-left">
            <p className="text-gray-500 dark:text-slate-300 mt-4 text-sm">
              {displayMessages.map((msg, index) => (
                <span key={index} className="block">
                  {msg.content}
                </span>
              ))}
            </p>
          </div>
        </div>

        {/* Feature 2: Sidebar Menu */}
        <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl shadow-md flex flex-col transition-colors">
          <h2 className="text-lg sm:text-xl font-bold text-blue-700 dark:text-blue-300 mb-4 text-left">
            Animated Sidebar Menu
          </h2>
          <div className="w-full max-w-xs mx-auto border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <Example />
          </div>
          <p className="text-center text-gray-500 dark:text-slate-300 mt-4 text-sm">
            Tap to expand and collapse this dynamic menu powered by Framer Motion.
          </p>
        </div>

        {/* Feature 3: Chart */}
        <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl shadow-md flex flex-col transition-colors">
          <h2 className="text-lg sm:text-xl font-bold text-blue-700 dark:text-blue-300 mb-4 text-left">
            Analytics Chart
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                <XAxis dataKey="name" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    color: "#111",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                  labelStyle={{ color: "#111" }}
                  wrapperStyle={{ zIndex: 20 }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  animationDuration={1500}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Feature 4: Lumi */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
          className="bg-white dark:bg-slate-800 hover:bg-[#111] dark:hover:bg-slate-700 group p-6 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-xl transition-all duration-300 text-left"
        >
          <div className="w-12 h-12 flex items-center justify-center bg-[#eaeaea] dark:bg-slate-700 group-hover:bg-white rounded-full text-[#111] dark:text-white group-hover:text-[#111] mb-4">
            <Bot size={24} />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-[#111] dark:text-white group-hover:text-white mb-3 transition-colors duration-300">
            AI-Powered Intelligence
          </h2>
          <p className="text-gray-600 dark:text-slate-300 group-hover:text-gray-300 text-sm leading-relaxed mb-3 transition-colors duration-300">
            Introducing <strong className="group-hover:text-white transition-colors">Lumi</strong> — our intelligent assistant framework designed to revolutionize decision-making and automation across your enterprise.
          </p>
          <p className="text-gray-600 dark:text-slate-300 group-hover:text-gray-300 text-sm leading-relaxed transition-colors duration-300">
            Tap to learn more about Lumi’s intelligent assistant capabilities — brought to life using Framer Motion.
          </p>
          <div className="mt-4">
            <button
              onClick={() => setModalOpen(true)}
              className="text-sm font-medium text-[#111] dark:text-white group-hover:text-white underline underline-offset-4 transition-colors duration-300"
            >
              Learn More →
            </button>
          </div>
        </motion.div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-slate-800 max-w-md w-full rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-2 right-3 text-gray-500 dark:text-gray-300 hover:text-black dark:hover:text-white text-xl"
            >
              ×
            </button>
            <h3 className="text-lg font-semibold text-[#111] dark:text-white mb-2">Meet Lumi</h3>
            <p className="text-gray-700 dark:text-slate-300 text-sm leading-relaxed">
              Lumi is an AI assistant built on local or cloud-hosted LLMs, capable of answering questions, automating workflows, and analyzing supply chain data in real-time. It powers platforms like LookApp and adapts to the unique needs of each business.
            </p>
          </div>
        </div>
      )}

      {/* Back to Home */}
      <div className="text-center mt-12">
        <Link
          to="/"
          className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition text-sm"
        >
          ← Back to Home
        </Link>
      </div>
    </div>
  );
};

export default DemoPage;
